import styles from './CreateWorkplace.module.css';
import Button from '../../Button/Button';
import { getAllWorkplacesUrl, WORKPLACE_TYPE_LABELS } from '../../../helpers/constants';
import React, { useEffect, useReducer, useState } from 'react';
import InputForm from '../../InputForm/InputForm';
import { SelectForm } from '../../SelectForm/SelectForm';
import { formReducer, INITIAL_STATE } from './CreateWorkplace.state';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import useApi from '../../../hooks/useApi.hook';
import ErrorMessage from '../../ErrorMessage/ErrorMessage';


const optionsType = Object.entries(WORKPLACE_TYPE_LABELS)
	.map(([value, label]) => ({ value, label }));

const optionsConditions = ['1', '2', '3.1', '3.2', '3.3', '3.4', '4']
	.map((value) => ({ value, label: value }));

export function CreateWorkplace({
	optionsProf, optionsDiv, setCreateModalOpen, setRefreshKey, currentWorkplace
}) {
	const api = useApi();
	const [error, setError] = useState(undefined);
	const [formState, dispatchForm] = useReducer(formReducer, INITIAL_STATE);
	const { values, isValid, isFormReadyToSubmit } = formState;
	const isEdit = !!currentWorkplace;

	useEffect(() => {
		if (currentWorkplace) {
			dispatchForm({ type: 'SET_FROM_WORKPLACE', payload: currentWorkplace });
		}
	}, [currentWorkplace]);

	const saveWorkplace = async (payload) => {
		try {
			if (isEdit) {
				await api.patch(`${getAllWorkplacesUrl}${currentWorkplace.id}`, payload);
			} else {
				await api.post(getAllWorkplacesUrl, payload);
			}
			setCreateModalOpen(false);
			setRefreshKey((prev) => prev + 1);
		} catch (e) {
			setError(
				e.response?.data?.detail || e.response?.data?.message || 'Неизвестная ошибка'
			);
		}
	};

	const onChange = (e) => {
		dispatchForm({type: 'SET_VALUE', payload: { [e.target.name]: e.target.value}});
		dispatchForm({type: 'SET_VALIDITY_FOR_FIELD', payload: e.target.name});
	};

	const selectValue = (name) => (e) => {
		dispatchForm({type: 'SET_VALUE', payload: { [name]: e.value}});
		dispatchForm({type: 'SET_VALIDITY_FOR_FIELD', payload: name});
	};

	const addWorkplace = (e) => {
		e.preventDefault();
		dispatchForm({type: 'SUBMIT'});
	};

	useEffect(() => {
		if (isFormReadyToSubmit) {
			// On create an empty optional field is simply omitted; on edit it has
			// to be sent as null, otherwise clearing it would be a no-op.
			const optional = (value) => (value || (isEdit ? null : undefined));
			const payload = {
				type: values.type,
				external_id: values.external_id.trim(),
				position: values.position.trim(),
				profession_id: Number(values.profession_id),
				division_id: Number(values.division_id),
				sout_card_number: optional(values.sout_card_number),
				working_conditions: optional(values.working_conditions),
				sheet_date: optional(values.sheet_date)
			};
			saveWorkplace(payload);
			dispatchForm({ type: 'CLEAR' });
		}
	}, [isFormReadyToSubmit, values]);

	const errorMessage = 'Обязательное поле';

	return (
		<div className={styles['create_workplace']}>
			<h1 className={styles['title']}>
				{isEdit ? 'Редактировать рабочее место' : 'Создать рабочее место'}
			</h1>
			<ErrorMessage
				error={error}
				onClose={() => setError(undefined)}
			/>
			<form onSubmit={addWorkplace} className={styles['form']}>
				<div className={styles['content']}>
					<div className={styles['left_panel']}>
						<span
							className={styles['span']}
							data-tooltip-content={errorMessage}
							data-tooltip-id="errorTooltipType"
						>
							Тип рабочего места*:
							<SelectForm
								value={optionsType.find(option => option.value === values.type) || null}
								options={optionsType}
								placeholder="Тип рабочего места"
								name='type'
								onChange={selectValue('type')}
								isValid={isValid.type}
							/>
							<Tooltip
								id="errorTooltipType"
								place="top-end"
								content={errorMessage}
								isOpen={!isValid.type}
								className={styles['my-tooltip']}
							/>
						</span>
						<span
							className={styles['span']}
							data-tooltip-content={errorMessage}
							data-tooltip-id="errorTooltipExternalId"
						>
							Код рабочего места*:
							<InputForm
								value={values.external_id}
								type="text"
								name='external_id'
								isValid={isValid.external_id}
								placeholder='Например, 352А'
								onChange={onChange}
							/>
							<Tooltip
								id="errorTooltipExternalId"
								place="top-end"
								content={errorMessage}
								isOpen={!isValid.external_id}
								className={styles['my-tooltip']}
							/>
						</span>
						<span
							className={styles['span']}
							data-tooltip-content={errorMessage}
							data-tooltip-id="errorTooltipPosition"
						>
							Должность*:
							<InputForm
								value={values.position}
								type="text"
								name='position'
								isValid={isValid.position}
								placeholder='Должность'
								onChange={onChange}
							/>
							<Tooltip
								id="errorTooltipPosition"
								place="top-end"
								content={errorMessage}
								isOpen={!isValid.position}
								className={styles['my-tooltip']}
							/>
						</span>
						<span
							className={styles['span']}
							data-tooltip-content={errorMessage}
							data-tooltip-id="errorTooltipProf"
						>
							Профессия*:
							<SelectForm
								value={optionsProf.find(
									option => Number(option.value) === Number(values.profession_id)) || null}
								options={optionsProf}
								placeholder="Профессия"
								name='profession_id'
								onChange={selectValue('profession_id')}
								isValid={isValid.profession_id}
							/>
							<Tooltip
								id="errorTooltipProf"
								place="top-end"
								content={errorMessage}
								isOpen={!isValid.profession_id}
								className={styles['my-tooltip']}
							/>
						</span>
					</div>
					<div className={styles['right_panel']}>
						<span
							className={styles['span']}
							data-tooltip-content={errorMessage}
							data-tooltip-id="errorTooltipDiv"
						>
							Подразделение*:
							<SelectForm
								value={optionsDiv.find(
									option => Number(option.value) === Number(values.division_id)) || null}
								options={optionsDiv}
								placeholder="Подразделение"
								name='division_id'
								onChange={selectValue('division_id')}
								isValid={isValid.division_id}
							/>
							<Tooltip
								id="errorTooltipDiv"
								place="top-end"
								content={errorMessage}
								isOpen={!isValid.division_id}
								className={styles['my-tooltip']}
							/>
						</span>
						<span className={styles['span']}>
							Класс условий труда:
							<SelectForm
								value={optionsConditions.find(
									option => option.value === values.working_conditions) || null}
								options={optionsConditions}
								placeholder="Класс условий труда"
								name='working_conditions'
								onChange={selectValue('working_conditions')}
							/>
						</span>
						<span className={styles['span']}>
							Номер карты СОУТ:
							<InputForm
								value={values.sout_card_number}
								type="text"
								name='sout_card_number'
								placeholder='Номер карты СОУТ'
								onChange={onChange}
							/>
						</span>
						<span className={styles['span']}>
							Дата карты:
							<InputForm
								value={values.sheet_date}
								type="date"
								name='sheet_date'
								placeholder='Дата карты'
								onChange={onChange}
							/>
						</span>
					</div>
				</div>

				<div className={styles['button']}>
					<Button>{isEdit ? 'Сохранить' : 'Создать'}</Button>
				</div>
			</form>
		</div>
	);
}